import * as THREE from 'three/build/three.module.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';

import { HDRCubeTextureLoader } from 'three/examples/jsm/loaders/HDRCubeTextureLoader.js';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { BloomPass } from 'three/examples/jsm/postprocessing/BloomPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { FXAAShader } from 'three/examples/jsm/shaders/FXAAShader.js';

import CameraControls from 'camera-controls';
CameraControls.install( { THREE: THREE } );

import { Entity, Light, Utils } from './index';

import fontJSON from 'three/examples/fonts/helvetiker_regular.typeface.json';






// If you want to create OBJKT's with different seeds, 
// you can access the creator and viewer wallet ids. 
// This values will only be injected once the piece 
// has been minted they will not work locally.
// If the user is not sync, the viewer comes in as false.
const creator = new URLSearchParams(window.location.search).get('creator');
const viewer = new URLSearchParams(window.location.search).get('viewer');

console.log('NFT created by', creator);
console.log('NFT viewed by', viewer);

const EPS = 1e-5;

const INTERSECT_POLL = 4;



class World {

	constructor({
		// todo: get typescript working!
		ambientIntensity = 0.25,
		ambientColor = 0x553333,
		backgroundColor = 0xeedddd,
		cameraType = "3rd-person",
		domElementId = null,
		useCameraLight = true,
		useHemisphereLight = false,
		mutedTone = true,
		useBloomPass = true,
		bloomIntensity = 0.5,
		cameraLightIntensity = 100,
		useFog = true,
		useHelpers = false,
		fogColor = 0xffdddd,
		isOutside = false,
		useLogBuffer = false,
	} = {}) {

		this.inited = false;

		/**
		 * World color
		 */
		

		this.scene = new THREE.Scene();
		this.backgroundColor = new THREE.Color(backgroundColor);
		this.scene.background = new THREE.Color(this.backgroundColor);

		if(useFog) {
			this.fogColor = new THREE.Color(fogColor);
			this.scene.fog = new THREE.Fog(this.fogColor, 10, 1500); 
		}		

		if(useHelpers) {
			const axesHelper = new THREE.AxesHelper(5);
			this.scene.add(axesHelper);
		}
		


		this.clock = new THREE.Clock();
		this.delta = 0;

		/**
		 * Camera
		 */

		this.camera = new THREE.PerspectiveCamera( 75, window.innerWidth / window.innerHeight, 0.5, 5000 );
		

		this.camera.position.set(0, 0, EPS);

		this.camera.position.set(0, 0, 15);
		
		this.scene.add(this.camera);


		if(useCameraLight) {
	    
		    const pointLight = new THREE.PointLight( 0xffffff );
			
			pointLight.intensity = cameraLightIntensity;
			pointLight.radius = 5;
			pointLight.decay = 2;
			pointLight.castShadow = true;

			pointLight.shadow.mapSize.x = 1024;
			pointLight.shadow.mapSize.y = 1024;
			pointLight.shadow.bias = -0.001;
			pointLight.shadow.radius = 5;
			pointLight.shadow.camera.near = 0.25;
			pointLight.shadow.camera.far = 500; 
			
			pointLight.position.set(.2, .5, .2);

			this.camera.add(pointLight);
			this.cameraLight = pointLight;		

		}


		/**
		 * Hemisphere light setting
		 */
		
		if(useHemisphereLight) {
			const hemiLight = new THREE.HemisphereLight( 0xffffbb, 0x080820, 1);
			this.scene.add(hemiLight);
		}


		/**
		 * Ambient light setting
		 */
		
		if(ambientIntensity) {
			this.ambientLight = new THREE.AmbientLight(ambientColor); // soft white light
			this.ambientLight.intensity = ambientIntensity;
			this.scene.add(this.ambientLight);
		}

		// todo - bokeh? https://discourse.threejs.org/t/very-thin-lines-look-jagged-antialiasing-not-working/6973/4


		/**
		 * Initialise renderer
		 */

  		this.renderer = new THREE.WebGLRenderer({
  			antialias: true,
  			logarithmicDepthBuffer: true
  		});
  		if(mutedTone) {
  			this.renderer.toneMapping = THREE.ReinhardToneMapping;
  		}
        this.renderer.toneMappingExposure = .85;
		this.renderer.shadowMap.enabled = false;
		this.renderer.shadowMapSoft = false;
		this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
		
		this.renderer.physicallyCorrectLights = true;
		this.renderer.logarithmicDepthBuffer = useLogBuffer;

		if(domElementId) {
			const el = document.getElementById(domElementId);
			this.renderer.setSize(el.offsetWidth, el.offsetHeight);
			this.container = el;
			this.container.appendChild(this.renderer.domElement);
		} else {
			this.renderer.setSize(window.innerWidth, window.innerHeight);
			this.container = document.body;
			this.container.appendChild(this.renderer.domElement);
		}



		this.composer = new EffectComposer(this.renderer);

		const renderPass = new RenderPass(this.scene, this.camera);
		

		//todo: add these as configurable options
		const effectFXAA = new ShaderPass(FXAAShader);
		//console.log(effectFXAA);
		effectFXAA.uniforms.resolution.value.set( 1 / window.innerWidth, 1 / window.innerHeight )

		
        
		this.composer.setSize(window.innerWidth, window.innerHeight);
		this.composer.addPass(renderPass);
		this.composer.addPass(effectFXAA);
		

		if(useBloomPass) {
			const bloomPass = new UnrealBloomPass(new THREE.Vector2(window.innerWidth, window.innerHeight), 1.5, 0.4, 0.85 );
			bloomPass.threshold = 0.21;
			bloomPass.strength = bloomIntensity;
			bloomPass.radius = bloomIntensity * 0.1;
			bloomPass.renderToScreen = true;
			this.composer.addPass(bloomPass);
		}

        /*const effectCopy = new ShaderPass(THREE.CopyShader);
        this.composer.addPass(effectCopy);*/


        


        /**
         * Set up the animation loop
         */
        
        this.frame = 0;

		this.renderer.setAnimationLoop(t => {			
			this.frame++;
			this.renderLoop(t)			
		});


		// Camera control setup
		this.cameraControls = new CameraControls(this.camera, this.renderer.domElement);

		if(cameraType == 'orbit') {
		

		} else if(cameraType == '3rd-person') {
		
			this.camera.position.set(0, 0, EPS);
			this.cameraControls.azimuthRotateSpeed = - 0.3; // negative value to invert rotation direction
			this.cameraControls.polarRotateSpeed   = - 0.3; // negative value to invert rotation direction
			this.cameraControls.truckSpeed = 1 / EPS * 3;
			this.cameraControls.mouseButtons.wheel = CameraControls.ACTION.ZOOM;
			this.cameraControls.touches.two = CameraControls.ACTION.TOUCH_ZOOM_TRUCK;
			this.cameraControls.saveState();

			
		}


		/**
		 * Set up the raycaster
		 */
		this.raycaster = new THREE.Raycaster();



		/**
		 * Skybox init
		 * Will only be used for external scenes
		 */
		/*const skyboxGeo = new THREE.BoxGeometry(10000, 10000, 10000);
  		const skybox = new THREE.Mesh(skyboxGeo);
		this.scene.add(skybox);*/

		/**
		 * Load some text/testing
		 */
		
		// For the camera captioning, tbc
		/*const loader = new THREE.FontLoader();
		console.log(require('three/examples/fonts/helvetiker_regular.typeface.json'));
		loader.load(fontJSON, font => {


		const textGeometry = new THREE.TextGeometry( 'Hello three.js!', {
			font: new THREE.Font(fontJSON),
			size: 80,
			height: 5,
			curveSegments: 12,
			bevelEnabled: true,
			bevelThickness: 10,
			bevelSize: 8,
			bevelOffset: 0,
			bevelSegments: 5
		});

		const textMaterial = new THREE.MeshPhongMaterial({
			color: 0x156289,
			emissive: 0x072534,
			side: THREE.DoubleSide,
			flatShading: true
		});
		
		const group = new THREE.Group();
		const textMesh = new THREE.Mesh(textGeometry, textMaterial);
		textMesh.scale.set(1/20, 1/20, 1/20);
		group.add(textMesh);

		group.position.x = 5;
		group.position.z = 5;

		this.camera.add(group);*/


		

		//});


		/**
		 * Scrubber offset
		 * tbc
		 */
		this.scrubberOffset = 0; // from -2 to 1



		/**
		 * World toggles
		 * tbc
		 */
		this.isCameraLightOn = false;
		this.cameraLight;


		/**
		 * Intersections TBC
		 */
		this.intersected = null;

		

		

		this.children = [];

		

        
        this.mixer = null;

        this.mixers = [];
        this.clipActions = []; 



		/**
		 * Mouse/position
		 */
		this.pointer = new THREE.Vector2();

		this.pointer = new THREE.Vector2();
		this.pointerLast = new THREE.Vector2();
		this.pointerDiff = new THREE.Vector2();

		this.pointerOut = false;
		this.isPointerDown = false;

		/**
		 * Camera rotation rate values
		 */
		this.azimuthSpeeds = [];
		this.lastAzimuthAngle = this.cameraControls.azimuthAngle;
		//this.azimuthSpeeds = [0];

		document.addEventListener('pointerout', (event) => {
			this.pointerOut = true;
		});

		document.addEventListener('pointerover', (event) => {
			this.pointerOut = false;
		});

		document.addEventListener('pointerdown', (event) => {
			this.isPointerDown = true;
		});

		document.addEventListener('pointerup', (event) => {
			this.isPointerDown = false;
		});

		document.addEventListener('pointermove', event => this.onPointerMove(event));
        

		this.cameraControls.addEventListener('update', event => this.cameraControlUpdate(event));

		window.addEventListener('resize', _ => {
			var width = window.innerWidth;
			var height = window.innerHeight;
			this.renderer.setSize(width, height);
			this.camera.aspect = width / height;
			this.camera.updateProjectionMatrix();
		});

		// Prevent touch scrolling parent window
		function preventBehavior(e) {
    		e.preventDefault(); 
		};

		document.addEventListener("touchmove", preventBehavior, {passive: false});

	}


	/**
	 * Add an entity, optional helpers etc.
	 * Still to work on the best method for this...
	 */
	addEntity(entity: Entity) {
		
		this.scene.add(entity.object);
		if(entity.helper) {
			this.scene.add(entity.helper);
		}

	}


	/**
	 * Fires whenever camera position updates
	 * @type {[type]}
	 */
	cameraControlUpdate(event) {

		//const lastAzimuth = this.lastAzimuthAngle;
		const azimuthDiff = this.cameraControls.azimuthAngle - this.lastAzimuthAngle;
		this.lastAzimuthAngle = this.cameraControls.azimuthAngle;

		if(this.azimuthSpeeds.length > 5) {
			this.azimuthSpeeds.pop();
		}

		this.azimuthSpeeds.unshift(azimuthDiff);
		
	}


	get azimuthSpeed() {
		const sum = this.azimuthSpeeds.reduce((a, b) => a + b, 0);
		return - sum / this.azimuthSpeeds.length;
	}



	/**
	 * [createPivot description]
	 */
	createPivot() {
		const geometry = new THREE.BoxGeometry(.01, .01, .01);
		const material = new THREE.MeshBasicMaterial({ transparent: true });
		const pivot = new THREE.Mesh(geometry, material);
		pivot.isVisible = false;
		return pivot;
	}


	/**
	 * Promise based external model loader
	 * @param {[type]} url [description]
	 */
	loadGLTF(url, {
		useDraco = false
	}) {
		return new Promise(resolve => {
			const loader = new GLTFLoader();
			if(useDraco) {
				const dracoLoader = new DRACOLoader();
				dracoLoader.setDecoderPath('https://raw.githubusercontent.com/mrdoob/three.js/dev/examples/js/libs/draco/')
				loader.setDRACOLoader( dracoLoader );
			}
			loader.load(url, resolve);
		});
	}


	/**
	 * Process a GLTF url.
	 * Returns a promise
	 */
	processGLTF(url, {
		scale = 1,
		addObjects = true,
		addAnimations = true,
		useDraco = false
	} = {}) {

		return this.loadGLTF(url, useDraco).then(data => {
			
			// Move this elsewhere...
			this.inited = true;

			const result = {
				cameras: [],
				meshes: []
				};

			if(scale !== 1) {
				data.scene.scale.set(scale, scale, scale);
			}

			// Traverse scene and process meshes/lighting/textures etc
			data.scene.traverse(n => this.traverseScene(n, result));
			
			if(addAnimations) {
				const mixer = new THREE.AnimationMixer(data.scene);
				this.mixers.push(mixer);
				// Set up the animation mixers for the scene data
				data.animations.forEach(animation => {
					
					const action = mixer.clipAction(animation); // access first animation clip
					this.clipActions.push(action);
					mixer.name = animation.name;
					mixer.timeScale = 1;
					
				    action.play();
				});
			}

			if(addObjects) {
				this.scene.add(data.scene);	
			}			

			return data;

		});

	}

	/**
	 * Check for intersection with an object/children
	 */
	intersects(object, {
		includeChildren = false
	} = {}) {

		let intersects = false;
		
		if(!Array.isArray(object)) {
			intersects = this.raycaster.intersectObject(object);
		} else {
			intersects = this.raycaster.intersectObjects(object);
		}
		
		
		if(intersects.length > 0) {
			return intersects;
		} else {
			return false;
		}
	}



	/**
	 * Run through the scene and perform necessary
	 * tasks on objects, lights etc.
	 * @param {[type]} n      [description]
	 * @param {[type]} result [description]
	 */
	traverseScene(n, result) {
		
		// Some messy hacky stuff to deal with initial worlds
		if(n.constructor.name == "Mesh") {

			n.receiveShadow = false;
			if(n.name != "Bulb") {
				n.castShadow = false;
			}
			n.material.smoothShading  = true;
			n.material.shading = THREE.SmoothShading;
			if(n.material.map) n.material.map.anisotropy = 16; 
			//n.geometry.computeVertexNormals(true);
			result.meshes.push(n);
		} else {
			//console.log('not mesh');
		}
		//console.log(n.constructor.name);
		if(n.constructor.name == "PointLight" || n.constructor.name == "SpotLight") {

			// Configure lights imported from blender
			// todo: figure out what the "uniforms" issue is
			n.castShadow = true;
			n.shadow.mapSize.x = 1024;
			n.shadow.mapSize.y = 1024;
			n.shadow.bias = -0.0001;
			n.shadow.radius = 5;

			n.shadow.camera.near = .01;//500;
			n.shadow.camera.far = 1000;//4000;
			n.shadow.camera.fov = 25;
			
		}

		if (n instanceof THREE.Camera){
            result.cameras.push(n);
        }
	}


	/**
	 * Create some handy values when moving the pointer
	 * @param {[type]} event [description]
	 */
	onPointerMove(event) {			
		this.pointer.x = ( event.clientX / window.innerWidth ) * 2 - 1;
		this.pointer.y =  - ( event.clientY / window.innerHeight ) * 2 + 1;

		this.raycaster.setFromCamera(this.pointer.clone(), this.camera);

		this.pointerDiff.x = this.pointerLast.x - this.pointer.x;
		this.pointerDiff.y = this.pointerLast.y - this.pointer.y;

		this.pointerLast = this.pointer.clone();
	}


	/**
	 * Render loop?
	 */
	renderLoop(t) {

		this.composer.render();

		this.delta = this.clock.getDelta();

		const updated = this.cameraControls.update(this.delta);

		this.mixers.forEach(mixer => {
			mixer.update(this.delta);
		});

		if(this.mixer) {
			this.mixer.update(this.delta);
		}

		// dunno if this is used?
		this.children.forEach(child => {
			child.renderLoop(t);
		});		
	}


	/**
	 * Used for updating materials
	 */
	setMaterial({target, bumpMap, bumpScale, map, metalness, repeat} : {
					target: String,
					bumpMap: String,
					bumpScale: Number,
					map: String,
					metalness: Number,
					repeat: Number
				}) {

		const textureLoader = new THREE.TextureLoader();

		this.scene.traverse(child => {

			if(child.material) {
				//console.log(child.material.name);
			}

			if(child.material && child.material.name === target) {

				// time consuming! Find a better way...
				child.material = child.material.clone();

				child.material.metalness = metalness;

				child.material.color.setHex(0xffffff * Math.random());

				const offset = new THREE.Vector2().random();
				const rotation = Math.PI * 2 * Math.random();

				child.material.side = THREE.DoubleSide;
				
				// Run this at start, not when loading models/
				textureLoader.load(map, function ( map ) {
					map.offset = offset;

					//map.rotation = rotation;

					map.wrapS = map.wrapT = THREE.RepeatWrapping;
					map.repeat.set(repeat, repeat);
					child.material.map = map;
					child.material.needsUpdate = true;
				});

				textureLoader.load(bumpMap, function ( map ) {
					map.offset = offset;
					//map.rotation = rotation;
					map.wrapS = map.wrapT = THREE.RepeatWrapping;
					map.repeat.set(repeat, repeat);
					child.material.bumpMap = map;
					child.material.bumpScale = bumpScale;
					child.material.needsUpdate = true;
				});

				/*child.material.bumpMap = THREE.ImageUtils.loadTexture(bumpMap);
				child.material.map = THREE.ImageUtils.loadTexture(map);*/
			}
		})

	}


	/**
	 * World toggles - find a cleaner way of doing all
	 */
	set cameraLightIntensity(val) {
		if((val === true || val >= 0) && this.cameraLight) {
			this.cameraLight.intensity = val;
		} else {
			this.cameraLight.intensity = 0;
		}
	}

	set ambientLightIntensity(val) {
		if(this.ambientLight) {
			this.ambientLight.intensity = val;
		}
	}
        
	
}

export default World;